/**
 * C06 — parent dashboard UI extension.
 *
 * Renders the NEW sections added by the C06 wiki spec into a pre-existing
 * container element. Designed to be *appended* to the legacy
 * src/learn/parent-dashboard.ts render (which already covers the weekly
 * stats + sparkline + faction bars).
 *
 * What this module adds:
 *   • 30 天總覽 (totalQ / totalXP / totalMin / activeDays / averages)
 *   • 弱點 KP top-3 from the wrong-answer log (fallback: studentKPs)
 *
 * Why a separate module: keeps the existing 530-LOC parent-dashboard.ts
 * (and its 23-test suite) untouched while letting future C06 v2 iterations
 * evolve the new sections without risking regression on the proven UI.
 */

import type { PlayerProgress } from '../../shared/types';
import {
  build30DaySummary,
  buildWeakKPTopN,
  type PeriodSummary,
  type WeakKPRow,
} from './parent-dashboard';
import { friendlyKPName } from '../data/kp-display-names';
import {
  lookupMoeMetadata,
  buildAbilityIndicatorAchievement,
  buildRecentWeekUnits,
  type AbilityIndicatorRow,
  type RecentUnitRow,
  type MoeMetadata,
} from './v2-helpers';

function fmtMin(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return '0 分';
  if (minutes < 60) return `${Math.round(minutes)} 分`;
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes - h * 60);
  return m === 0 ? `${h} 小時` : `${h} 小時 ${m} 分`;
}

function summaryRowHTML(label: string, value: string): string {
  return `<div class="stat-item"><div class="stat-label">${label}</div><div class="stat-value">${value}</div></div>`;
}

/**
 * Render the C06 30-day section into a div with the supplied class.
 * Returns the new element so the caller can insert it where they like
 * in the parent dashboard. Pure DOM construction — no event wiring.
 */
export function buildMonthlySummarySection(summary: PeriodSummary): HTMLElement {
  const section = document.createElement('div');
  section.className = 'parent-section monthly-summary';
  section.setAttribute('data-testid', 'monthly-summary');
  section.innerHTML = `
    <h3>📅 30 天總覽 (${summary.dateRange.from} ~ ${summary.dateRange.to})</h3>
    <div class="weekly-stats">
      ${summaryRowHTML('總答題', String(summary.totalQuestions))}
      ${summaryRowHTML('總修煉值', String(summary.totalXP))}
      ${summaryRowHTML('遊戲時長', fmtMin(summary.totalMinutes))}
      ${summaryRowHTML('活躍天數', `${summary.activeDays} / ${summary.windowDays}`)}
      ${summaryRowHTML('活躍日均題數', String(Math.round(summary.avgQuestionsPerActiveDay)))}
      ${summaryRowHTML('活躍日均時長', fmtMin(summary.avgMinutesPerActiveDay))}
    </div>
  `;
  return section;
}

// ── HTML attribute escaping (tooltip text comes from authored data) ─────────
function escAttr(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Compose the `title` attribute payload for a weak-KP card.
 * Format (multi-line via newline; renders in browser native tooltip):
 *
 *   學習內容：N-3-9
 *   學習表現：n-II-9 (估算，待 S3 補完)
 *   說明：50-200 字 NAER 描述...
 *
 * Exported so tests can assert exact string composition without DOM.
 */
export function composeWeakKPTooltip(meta: MoeMetadata): string {
  const lines: string[] = [];
  lines.push(`學習內容：${meta.moeContentCode}`);
  if (meta.moePerformanceCode) {
    const tag = meta.source === 'v1-estimated' ? '（估算，待 S3 補完）' : '';
    lines.push(`學習表現：${meta.moePerformanceCode}${tag}`);
  } else {
    lines.push('學習表現：(待 S3 補完)');
  }
  if (meta.description && meta.description.length > 0) {
    lines.push(`說明：${meta.description}`);
  } else {
    lines.push('說明：(待 S3 補完)');
  }
  return lines.join('\n');
}

/**
 * Render the C06 弱點 KP top-N section. Uses the wrong-answer log first
 * (so the parent sees what their kid recently struggled with), then
 * falls back to all-time mastery for legacy saves with empty mistakes.
 *
 * S10 addition: each row gets a hover tooltip via `title=` carrying the
 * 教育部代碼 + description, sourced from V2 metadata (or estimated from
 * V1 with a pending marker).
 */
export function buildWeakKPSection(rows: WeakKPRow[]): HTMLElement {
  const section = document.createElement('div');
  section.className = 'parent-section weak-kp-section';
  section.setAttribute('data-testid', 'weak-kp-top');
  const items = rows.length === 0
    ? '<li style="color:#888">尚無弱點資料</li>'
    : rows.map((r) => {
        const name = friendlyKPName(r.kpId);
        const wrongLabel = r.wrongCount > 0 ? `${r.wrongCount} 次錯誤` : '尚無錯誤紀錄';
        const masteryLabel = r.totalAttempts > 0 ? `熟練度 ${r.mastery}%` : '尚未練習';
        const meta = lookupMoeMetadata(r.kpId);
        const tooltip = composeWeakKPTooltip(meta);
        const codeBadge = meta.moeContentCode === 'pending'
          ? '<span class="moe-code pending" style="color:#888">代碼待補</span>'
          : `<span class="moe-code">${escAttr(meta.moeContentCode)}</span>`;
        return `<li title="${escAttr(tooltip)}" data-kp-id="${escAttr(r.kpId)}" data-moe-content-code="${escAttr(meta.moeContentCode)}"><strong>${name}</strong> ${codeBadge} — ${masteryLabel} · ${wrongLabel}</li>`;
      }).join('');
  section.innerHTML = `
    <h3>🎯 弱點 KP Top ${rows.length || 3}（近 30 天）</h3>
    <ul class="dash-list">${items}</ul>
  `;
  return section;
}

/**
 * S10 — 能力指標達成度 section. Lists 學生能 ___ verb-layer indicators
 * grouped by `moePerformanceCode`, with achievement % per row.
 */
export function buildAbilityIndicatorSection(rows: AbilityIndicatorRow[]): HTMLElement {
  const section = document.createElement('div');
  section.className = 'parent-section ability-indicator-section';
  section.setAttribute('data-testid', 'ability-indicators');
  const items = rows.length === 0
    ? '<li style="color:#888">尚未開始練習</li>'
    : rows.map((r) => {
        const pendingTag = r.pending ? '<span style="color:#aaa"> (待 S3 補完)</span>' : '';
        const practiceNote = r.practicedKPCount === 0
          ? '<span style="color:#888">尚未練習</span>'
          : `${r.practicedKPCount}/${r.kpCount} KP 已練 · 達成 ${r.achievementPct}%`;
        const codeLabel = r.moePerformanceCode === 'pending' ? '(待 S3 補完)' : r.moePerformanceCode;
        return `<li data-perf-code="${escAttr(r.moePerformanceCode)}"><strong>${escAttr(r.abilitySentence)}</strong>${pendingTag}<br><small>${escAttr(codeLabel)} · ${practiceNote}</small></li>`;
      }).join('');
  section.innerHTML = `
    <h3>📜 能力指標達成度</h3>
    <ul class="dash-list ability-list">${items}</ul>
  `;
  return section;
}

/**
 * S10 — 最近一週學了哪些單元 list. Aggregated by {subject, chapter}.
 */
export function buildRecentUnitsSection(rows: RecentUnitRow[]): HTMLElement {
  const section = document.createElement('div');
  section.className = 'parent-section recent-units-section';
  section.setAttribute('data-testid', 'recent-units');
  const SUBJECT_EMOJI: Record<string, string> = {
    math: '⚡', chinese: '🔮', english: '🤖', nature: '🌀', social: '🏰',
  };
  const items = rows.length === 0
    ? '<li style="color:#888">最近一週尚無學習紀錄</li>'
    : rows.map((r) => {
        const emoji = SUBJECT_EMOJI[r.subject] ?? '📘';
        const wk = r.weekRangeLabel ? ` · ${r.weekRangeLabel}` : '';
        return `<li data-subject="${escAttr(r.subject)}" data-chapter="${r.chapterNumber}">${emoji} <strong>第 ${r.chapterNumber} 章 ${escAttr(r.chapterTitle)}</strong> — ${r.kpCount} KP · ${r.attempts} 題${wk} · 最後練習 ${escAttr(r.lastReviewedISO)}</li>`;
      }).join('');
  section.innerHTML = `
    <h3>📚 最近一週學了哪些單元</h3>
    <ul class="dash-list recent-units-list">${items}</ul>
  `;
  return section;
}

/**
 * Append the C06 sections (30-day summary + weak-KP top-3) to an existing
 * parent-dashboard root. Idempotent: removes any previously rendered C06
 * sections in the same root before re-rendering, so a parent toggling
 * something elsewhere doesn't double-stack.
 *
 * @param root      the parent-dashboard root element (already populated
 *                  with the legacy 7-day sections)
 * @param progress  loaded PlayerProgress; if null the function is a no-op
 * @param nowISO    for testing — clock injection
 * @param nowMs     for testing — Date.now() injection
 */
export function appendC06Sections(
  root: HTMLElement,
  progress: PlayerProgress | null,
  nowISO?: string,
  nowMs?: number,
): void {
  if (!progress) return;

  // Idempotency: nuke any prior C06 + S10 sections first.
  root.querySelectorAll(
    '.monthly-summary,.weak-kp-section,.ability-indicator-section,.recent-units-section',
  ).forEach((el) => el.remove());

  const summary30 = build30DaySummary(progress, nowISO);
  const weakRows = buildWeakKPTopN(progress, 3, 30, nowMs);
  const abilityRows = buildAbilityIndicatorAchievement(progress);
  const recentUnits = buildRecentWeekUnits(progress, 7, nowISO);

  // Insert the C06 + S10 sections immediately AFTER the existing
  // weekly-report section if it exists. Display order top→bottom:
  //   weekly-report (existing)
  //   → monthly summary (30d)
  //   → weak KP top-3   (with V2 moe-code tooltips)
  //   → ability indicators (能力指標達成度)
  //   → recent units list  (最近一週)
  // We insert with insertAdjacentElement('afterend', ...) in REVERSE
  // order so the final visible order matches the list above.
  const weekly = root.querySelector('.weekly-report');
  const monthly = buildMonthlySummarySection(summary30);
  const weak = buildWeakKPSection(weakRows);
  const ability = buildAbilityIndicatorSection(abilityRows);
  const recent = buildRecentUnitsSection(recentUnits);
  if (weekly && weekly.parentElement === root) {
    weekly.insertAdjacentElement('afterend', recent);
    weekly.insertAdjacentElement('afterend', ability);
    weekly.insertAdjacentElement('afterend', weak);
    weekly.insertAdjacentElement('afterend', monthly);
  } else {
    root.prepend(recent);
    root.prepend(ability);
    root.prepend(weak);
    root.prepend(monthly);
  }
}
